import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { GameOutput, PlayOutput } from '../generated-api';
import { computeDustyShelf, dustAge, DUSTY_AFTER_DAYS } from '../utils/dustyShelf';

const MAX_SHOWN = 12;

interface DustyShelfSectionProps {
  games: GameOutput[];
  plays: PlayOutput[];
}

/** Games page: former favourites that gathered dust plus owned games never played, with a picker shortcut. */
const DustyShelfSection: React.FC<DustyShelfSectionProps> = ({ games, plays }) => {
  const [owner, setOwner] = useState<string | null>(null);

  const allOwners = useMemo(() => Array.from(new Set(games.flatMap(g => g.owners || []))).sort(), [games]);
  const everything = useMemo(() => computeDustyShelf(games, plays), [games, plays]);
  const shelf = useMemo(() => computeDustyShelf(games, plays, { owner }), [games, plays, owner]);

  if (everything.length === 0) return null;

  const pickerLink = `/tools/game-picker?preset=dusty${owner ? `&owner=${encodeURIComponent(owner)}` : ''}`;

  return (
    <section className="mb-8 bg-surface shadow rounded-lg p-4 sm:p-6" aria-labelledby="dusty-shelf-heading">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id="dusty-shelf-heading" className="text-fluid-h2 font-semibold text-heading">
            <span aria-hidden="true">🕸️ </span>Dusty shelf
          </h2>
          <p className="text-sm text-body mt-1">
            Favourites nobody played in {Math.round(DUSTY_AFTER_DAYS / 30)}+ months, and owned games never played
          </p>
        </div>
        {shelf.length >= 2 && (
          <Link
            data-track="dusty_shelf_spin"
            to={pickerLink}
            className="flex-shrink-0 inline-flex items-center min-h-[44px] px-3 rounded-full text-sm font-medium text-white bg-primary-700 hover:bg-primary-800"
          >
            <span aria-hidden="true">🎲&nbsp;</span>Spin
          </Link>
        )}
      </div>

      {allOwners.length > 1 && (
        <div className="flex gap-1.5 overflow-x-auto mt-3 -mx-1 px-1 pb-1" role="group" aria-label="Owner">
          {[null, ...allOwners].map(o => (
            <button
              data-track="dusty_shelf_owner"
              key={o ?? 'all'}
              onClick={() => setOwner(o)}
              aria-pressed={owner === o}
              className={`flex-shrink-0 min-h-[36px] px-3 text-sm rounded-full border transition-colors ${
                owner === o
                  ? 'bg-primary-700 text-white border-primary-700'
                  : 'bg-surface text-body border-edge-input hover:bg-surface-hover'
              }`}
            >
              {o ?? 'All'}
            </button>
          ))}
        </div>
      )}

      {shelf.length === 0 ? (
        <p className="text-sm text-muted mt-4">Nothing dusty on {owner}'s shelf.</p>
      ) : (
        <ul className="flex gap-3 overflow-x-auto mt-4 -mx-1 px-1 pb-2 snap-x">
          {shelf.slice(0, MAX_SHOWN).map(d => (
            <li key={d.game.id} className="flex-shrink-0 w-32 snap-start">
              <Link data-track="dusty_shelf_game" to={`/games/${d.game.id}`} className="block group" data-testid={`dusty-${d.game.id}`}>
                <div className="h-32 w-32 rounded-xl bg-surface-skeleton flex items-center justify-center overflow-hidden">
                  {d.game.imageUrl ? (
                    <img src={d.game.imageUrl} alt="" className="max-w-full max-h-full object-contain" loading="lazy" />
                  ) : (
                    <span className="text-4xl" aria-hidden="true">🎲</span>
                  )}
                </div>
                <div className="mt-1.5 text-sm font-medium text-heading truncate group-hover:underline">{d.game.name}</div>
                <div className="text-xs text-muted">
                  {d.kind === 'forgotten'
                    ? `${d.playCount} plays · ${dustAge(d.daysSince ?? 0)} ago`
                    : 'Never played'}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {shelf.length > MAX_SHOWN && (
        <p className="text-xs text-muted mt-1">+{shelf.length - MAX_SHOWN} more in the Game Picker</p>
      )}
    </section>
  );
};

export default DustyShelfSection;
